import { ApiService } from './ApiService';
import { refreshOrEndSession } from './ApiService';
import {
  GraphqlRequestError,
  graphqlOperationName,
  type IGraphqlErrorPayload,
} from './graphqlRequestError';
import { requireRootFields } from './graphqlRootFields';
import { requestTolerantOfOlderServer } from './olderServer';
import { assertOnlineForWrite } from '@/shell/shellOffline';
import { noteGraphqlRead } from '@/shell/bootReport';

/** Whether `document` opens with the `mutation` keyword — a read must still work from the cache the
 * HTTP layer already has; a write is what F-0236 (AC-11) refuses while offline. */
const IS_MUTATION = /^\s*mutation\b/;

type IGraphqlError = IGraphqlErrorPayload;

/**
 * How long a read on its way may be joined (I-1830 SBG-6): the duplicates it removes were asked for
 * 1 ms apart. A read older than this is not joined, so a screen's Retry, offered only after RD-33's
 * 10 s deadline (`LIST_LOAD_DEADLINE_MS`) or a failure, always sends its own read and never waits on
 * the one that did not answer (gate 754).
 */
export const READ_JOIN_WINDOW_MS = 2_000;

interface IGraphqlResponse<T> {
  data?: T;
  errors?: IGraphqlError[];
}

/**
 * Thin GraphQL transport on top of ApiService so the work-item API reuses the
 * same base URL, bearer token and refresh-on-401 handling as the REST clients.
 *
 * An answer missing a root field the query asked for is rejected here, by name (I-0748), so every
 * caller's `res.field` is a value or `null` and never `undefined`: a malformed answer reaches the
 * caller's load-failure path instead of crashing a page while it draws.
 */
class GraphqlClient extends ApiService {
  /**
   * I-1830 SBG-6 (`docs/concept-local-stable-operations.md`): a read asked for while the same read
   * (same document, same variables) is already on its way joins it instead of being sent again. One
   * Work items page load sent its first `WorkItemsPage` twice 1 ms apart, and `Releases` and
   * `WorkAreaWaitingItems` twice each; a list read is what the API holds a heap for. A write
   * empties the map when it starts and when it ends, so a read asked for after a write never
   * receives an answer that was read before it.
   */
  private readonly readsInFlight = new Map<string, { read: Promise<unknown>; sentAt: number }>();

  /** Ends the connection listeners below (`dispose`). */
  private stopListening: () => void = () => {};

  constructor() {
    super();
    /*
     * RD-38 (gate 754): a read sent before the connection changed is never joined. The read on its
     * way when the device went offline may never answer; a screen that asks again once the
     * connection is back must send its own read, not wait on that one.
     */
    if (typeof window !== 'undefined') {
      const forgetReadsInFlight = () => this.readsInFlight.clear();
      // Removed by name, not by an AbortSignal option: a test realm may pair jsdom's window with a
      // different AbortSignal, and a listener option that is not that window's own signal throws.
      window.addEventListener('offline', forgetReadsInFlight);
      window.addEventListener('online', forgetReadsInFlight);
      this.stopListening = () => {
        window.removeEventListener('offline', forgetReadsInFlight);
        window.removeEventListener('online', forgetReadsInFlight);
      };
    }
  }

  /** Stops listening for connection changes and forgets every read in flight. */
  dispose(): void {
    this.stopListening();
    this.readsInFlight.clear();
  }

  async request<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
    if (IS_MUTATION.test(query)) {
      assertOnlineForWrite();
      this.readsInFlight.clear();
      try {
        return await this.send<T>(query, variables);
      } finally {
        this.readsInFlight.clear();
      }
    }
    const key = `${query}\u0000${JSON.stringify(variables ?? null)}`;
    const joined = this.readsInFlight.get(key);
    // Each caller gets its own copy: a page that edits what it was given never edits another's.
    if (joined && Date.now() - joined.sentAt < READ_JOIN_WINDOW_MS) {
      return (joined.read as Promise<T>).then((data) => structuredClone(data));
    }
    noteGraphqlRead(graphqlOperationName(query));
    const read = this.send<T>(query, variables);
    this.readsInFlight.set(key, { read, sentAt: Date.now() });
    const forget = () => {
      if (this.readsInFlight.get(key)?.read === read) this.readsInFlight.delete(key);
    };
    read.then(forget, forget);
    return read;
  }

  private send<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
    // I-1383: a server behind this web is asked again without the fields it does not know.
    return requestTolerantOfOlderServer(query, (sent) => this.requestOnce<T>(sent, variables));
  }

  private async requestOnce<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
    const res = await this.post<IGraphqlResponse<T>>('/graphql', { query, variables });
    if (isGraphqlAuthError(res.errors)) {
      // F-0534 PPD-3 (I-1311): a refresh refused here ends the session as the interceptor's does.
      await refreshOrEndSession();
      const retry = await this.post<IGraphqlResponse<T>>('/graphql', { query, variables });
      if (retry.errors?.length) {
        throw new GraphqlRequestError(retry.errors[0], graphqlOperationName(query));
      }
      if (!retry.data) {
        throw new Error('GraphQL response contained no data');
      }
      return requireRootFields(query, retry.data);
    }
    if (res.errors?.length) {
      throw new GraphqlRequestError(res.errors[0], graphqlOperationName(query));
    }
    if (!res.data) {
      throw new Error('GraphQL response contained no data');
    }
    return requireRootFields(query, res.data);
  }
}

const graphqlClient = new GraphqlClient();

/** A client of its own, for a test that disposes it; the app uses the one above for its lifetime. */
export function createGraphqlClient(): Pick<GraphqlClient, 'request' | 'dispose'> {
  return new GraphqlClient();
}

/**
 * Anything that answers a GraphQL document: the main deployment's `gql` below, or a work area's own
 * connection's requester (`workAreaApis`, I-1120). A work-area API is built over one of these, so the
 * same methods reach whichever deployment holds the work area.
 */
export type GraphqlRequesterType = <T>(
  query: string,
  variables?: Record<string, unknown>,
) => Promise<T>;

export function gql<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  return graphqlClient.request<T>(query, variables);
}

// ⛔ "Forbidden" is not here: a refused read is not a refused credential, and refreshing on it can
// end a session that was fine (AGM-41).
function isGraphqlAuthError(errors?: IGraphqlError[]): boolean {
  if (!errors?.length) return false;
  return errors.some((error) => /unauthorized|jwt|token/i.test(error.message));
}
